import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { openPricingDialog, setActiveTab } from '../../store/slices/pricingSlice';
import { openBillingPortal } from '../../utils/openBillingPortal';
import {
  formatPlanType,
  formatRenewalDate,
  formatSubscriptionStatus,
  isSubscriptionPlanType,
} from '../../utils/planLabels';
import { API_URL, parseCheckoutResponse } from '../../utils/apiUrl';
import { message } from '../Message';
import AppAccountLayout from '../AppAccountLayout';
import NewPricingStripe from '../NewPricingStripe';
import Spinner from '../Spinner';

const SUBSCRIPTION_PLANS = [
  {
    name: 'Premium',
    price: '$27',
    period: '/ month',
    credits: '2,500 pro credits',
    type: 'premium_monthly',
    description: 'For individuals who need regular professional headshots.',
  },
  {
    name: 'Business',
    price: '$97',
    period: '/ month',
    credits: '15,000 pro credits',
    type: 'business_monthly',
    description: 'For teams and power users with higher volume needs.',
    highlighted: true,
  },
];

const cardStyle = {
  background: '#1a1a1a',
  border: '1px solid #333',
  borderRadius: '12px',
  padding: '24px',
};

const BillingPage = ({ firebase, userData: initialUserData, setUser }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [userData, setUserData] = useState(initialUserData);
  const [isLoading, setIsLoading] = useState(!initialUserData);
  const [isBuying, setIsBuying] = useState(false);
  const [isOpeningPortal, setIsOpeningPortal] = useState(false);

  useEffect(() => {
    if (!firebase?.auth?.currentUser) {
      return undefined;
    }

    firebase
      .getUser(firebase.auth.currentUser.uid)
      .then((doc) => {
        if (doc.exists()) {
          const data = { uid: doc.id, ...doc.data() };
          setUserData(data);
          setUser(data);
        }
      })
      .finally(() => setIsLoading(false));
  }, [firebase, setUser]);

  const hasActiveSubscription = Boolean(userData?.hasSubscription);
  const planName = formatPlanType(userData?.type);
  const renewalDate = formatRenewalDate(userData?.subscriptionEndDate);
  const statusLabel = formatSubscriptionStatus(userData);

  const handleManageBilling = async () => {
    setIsOpeningPortal(true);
    try {
      await openBillingPortal({
        email: userData?.email,
        uid: userData?.uid,
      });
    } catch (error) {
      message.error(error.message || t('billingPage.portalError'));
    } finally {
      setIsOpeningPortal(false);
    }
  };

  const handleViewAllPlans = () => {
    dispatch(setActiveTab('1'));
    dispatch(openPricingDialog());
  };

  const handleSubscribe = (plan) => {
    if (!userData?.email || !userData?.uid) {
      message.error(t('billingPage.signInRequired'));
      return;
    }

    setIsBuying(true);
    fetch(`${API_URL}/create-checkout-session`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: userData.email,
        uid: userData.uid,
        product_type: plan.type,
      }),
    })
      .then(parseCheckoutResponse)
      .then((url) => {
        window.location.href = url;
      })
      .catch((error) => {
        message.error(error.message || t('billingPage.checkoutError'));
      })
      .finally(() => setIsBuying(false));
  };

  if (isLoading || !userData) {
    return <Spinner />;
  }

  return (
    <>
      <AppAccountLayout
        title={t('billingPage.title')}
        subtitle={t('billingPage.subtitle')}
        backLabel={t('billingPage.backToApp')}
        userData={userData}
        t={t}
      >
        <div style={{ display: 'grid', gap: '24px' }}>
          <section style={cardStyle}>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '16px' }}>
              {t('billingPage.currentPlan')}
            </h2>

            {hasActiveSubscription && planName ? (
              <div style={{ display: 'grid', gap: '12px' }}>
                <div>
                  <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('billingPage.plan')}</div>
                  <div style={{ fontSize: '1.125rem', fontWeight: 600 }}>{planName}</div>
                </div>
                <div>
                  <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('billingPage.status')}</div>
                  <div>{statusLabel}</div>
                </div>
                {renewalDate ? (
                  <div>
                    <div style={{ color: '#888', fontSize: '0.875rem' }}>
                      {userData.autoRenew === false
                        ? t('billingPage.accessUntil')
                        : t('billingPage.renewsOn')}
                    </div>
                    <div>{renewalDate}</div>
                  </div>
                ) : null}
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('billingPage.proCredits')}</div>
                    <div>{userData.proCredits ?? 0}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('billingPage.basicCredits')}</div>
                    <div>{userData.basicCredits ?? 0}</div>
                  </div>
                </div>
                <button
                  type="button"
                  className="btn btn-warning btn-sm rounded-pill mt-2"
                  onClick={handleManageBilling}
                  disabled={isOpeningPortal}
                >
                  {isOpeningPortal ? t('billingPage.openingPortal') : t('billingPage.manageBilling')}
                </button>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                <p style={{ color: '#ccc', margin: 0 }}>
                  {isSubscriptionPlanType(userData.type)
                    ? t('billingPage.expiredSubscription')
                    : t('billingPage.noSubscription')}
                </p>
                <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('billingPage.proCredits')}</div>
                    <div>{userData.proCredits ?? 0}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', fontSize: '0.875rem' }}>{t('billingPage.basicCredits')}</div>
                    <div>{userData.basicCredits ?? 0}</div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {!hasActiveSubscription ? (
            <section>
              <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '8px' }}>
                {t('billingPage.subscribeTitle')}
              </h2>
              <p style={{ color: '#aaa', marginBottom: '20px' }}>{t('billingPage.subscribeSubtitle')}</p>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '16px',
                }}
              >
                {SUBSCRIPTION_PLANS.map((plan) => (
                  <div
                    key={plan.type}
                    style={{
                      ...cardStyle,
                      borderColor: plan.highlighted ? '#facc15' : '#333',
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: '1.125rem' }}>{plan.name}</div>
                    <div style={{ margin: '8px 0' }}>
                      <span style={{ fontSize: '1.75rem', fontWeight: 700 }}>{plan.price}</span>
                      <span style={{ color: '#888' }}>{plan.period}</span>
                    </div>
                    <div style={{ color: '#ccc', marginBottom: '8px' }}>{plan.credits}</div>
                    <p style={{ color: '#999', fontSize: '0.9rem' }}>{plan.description}</p>
                    <button
                      type="button"
                      className={`btn btn-sm rounded-pill ${plan.highlighted ? 'btn-warning' : 'btn-outline-light'}`}
                      onClick={() => handleSubscribe(plan)}
                      disabled={isBuying}
                    >
                      {t('billingPage.subscribe')}
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                className="btn btn-link text-white mt-3 px-0"
                onClick={handleViewAllPlans}
              >
                {t('billingPage.viewAllPlans')}
              </button>
            </section>
          ) : null}

          {hasActiveSubscription ? (
            <section style={{ ...cardStyle, background: '#141414' }}>
              <h2 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '8px' }}>
                {t('billingPage.paymentHistoryTitle')}
              </h2>
              <p style={{ color: '#aaa', marginBottom: '16px' }}>{t('billingPage.paymentHistoryBody')}</p>
              <button
                type="button"
                className="btn btn-outline-light btn-sm rounded-pill"
                onClick={handleManageBilling}
                disabled={isOpeningPortal}
              >
                {t('billingPage.openStripePortal')}
              </button>
            </section>
          ) : null}
        </div>
      </AppAccountLayout>

      <NewPricingStripe
        t={t}
        userData={userData}
        firebase={firebase}
        startHeadshots={() => {}}
      />
    </>
  );
};

export default BillingPage;
